"use client";

import { useCallback, useState, type ChangeEvent, type FormEvent } from "react";
import { useUsersLogin } from "../hooks";
import type { UsersLoginSuccess } from "../client";

export interface LoginFormProps {
  /**
   * Callback invoked when login succeeds
   */
  onLoginSuccess: (session: UsersLoginSuccess) => void;
  
  /**
   * Title for the login form
   * @default "Sign in"
   */
  title?: string;
  
  /**
   * Description text for the login form
   */
  description?: string;
  
  /**
   * Whether to show the credentials helper card with test credentials
   * @default false
   */
  showCredentialsHelper?: boolean;
  
  /**
   * Initial email value
   * @default ""
   */
  initialEmail?: string;
  
  /**
   * Initial password value
   * @default ""
   */
  initialPassword?: string;

  /**
   * Custom class name for the form container
   */
  className?: string;

  /**
   * Children to render below the form (e.g., additional info cards)
   */
  children?: React.ReactNode;
}

interface FormState {
  email: string;
  password: string;
}

/**
 * Shared login form component that can be used across apps
 * Uses @project-x/auth for authentication and @project-x/ui-kit for UI components
 */
export function LoginForm({
  onLoginSuccess,
  title = "Sign in",
  description,
  showCredentialsHelper = false,
  initialEmail = "",
  initialPassword = "",
  className,
  children,
}: LoginFormProps): React.ReactElement {
  const [formState, setFormState] = useState<FormState>({
    email: initialEmail,
    password: initialPassword,
  });
  const { login, isLoading, error } = useUsersLogin();

  const handleChange = useCallback((field: keyof FormState) => {
    return (event: ChangeEvent<HTMLInputElement>) => {
      setFormState(state => ({ ...state, [field]: event.target.value }));
    };
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const result = await login(formState);
      if (result) {
        onLoginSuccess(result);
      }
    },
    [formState, login, onLoginSuccess],
  );

  return (
    <div className={className}>
      <form onSubmit={handleSubmit} action="javascript:void(0)" className="space-y-6">
        {(title || description) && (
          <div className="space-y-3">
            {title && <h2 className="text-2xl font-bold">{title}</h2>}
            {description && <p className="text-sm opacity-75">{description}</p>}
          </div>
        )}
        
        <div className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-2">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              value={formState.email}
              onChange={handleChange("email")}
              required
              disabled={isLoading}
              className="w-full px-3 py-2 border rounded-md bg-white/5 border-white/10 focus:border-white/30 focus:outline-none focus:ring-2 focus:ring-white/20 disabled:opacity-50"
            />
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-2">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={formState.password}
              onChange={handleChange("password")}
              required
              disabled={isLoading}
              className="w-full px-3 py-2 border rounded-md bg-white/5 border-white/10 focus:border-white/30 focus:outline-none focus:ring-2 focus:ring-white/20 disabled:opacity-50"
            />
          </div>
        </div>

        {error && (
          <div className="border border-rose-500/40 bg-rose-500/10 text-sm text-rose-200 px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-600/50 text-white font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          {isLoading ? "Signing in..." : "Sign in"}
        </button>
      </form>

      {showCredentialsHelper && (
        <div className="mt-6 p-4 bg-white/5 rounded-md border border-white/10">
          <h3 className="text-sm font-semibold mb-2">Development Credentials</h3>
          <div className="text-xs opacity-75 space-y-1">
            <p>
              <span className="font-medium">Email:</span> test.user@example.com
            </p>
            <p>
              <span className="font-medium">Password:</span> P@ssw0rd!123
            </p>
          </div>
        </div>
      )}

      {children}
    </div>
  );
}

